import { useState, useEffect } from 'react';
import { Save, Globe, AlertTriangle, Network } from 'lucide-react';
import Modal from '../../common/Modal';
import edgeService from '../../../services/edge.service';

interface LogicalInterface {
  id: string;
  name: string;  // API returns 'name' not 'logical_name'
  port_type?: string;
  role?: string;
  type?: string;
  ipv4?: string;
  description?: string;
  physicalInterface?: string;
}

interface InsideInterfaceOption {
  name: string;
  type: 'physical' | 'wireguard';
  enabled: boolean;
}

interface SourceNATModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
  subscription?: any;
  mode: 'add' | 'edit';
  edgeId: string;
  /**
   * Pre-locked WAN interface — used when the modal is embedded inside the
   * parent Logical Interface modal. Hides the WAN selector.
   */
  lockedInterfaceId?: string;
  /**
   * Draft mode: the parent WAN interface doesn't exist yet — it's being
   * composed with this rule for a single atomic commit. The draft supplies the
   * interface name (for out_interface_name) and role so we don't depend on the
   * server list; logical_port_id is left empty for the commit endpoint to
   * assign.
   */
  draftInterface?: { name: string; role?: string; ipv4?: string; ipv4Aliases?: any[] };
}

const SourceNATModal = ({ isOpen, onClose, onSave, subscription, mode, edgeId, lockedInterfaceId, draftInterface }: SourceNATModalProps) => {
  const isDraft = !!draftInterface && !lockedInterfaceId;
  const [logicalInterfaces, setLogicalInterfaces] = useState<LogicalInterface[]>([]);
  const [loading, setLoading] = useState(false);

  // Inside interface selection state (for NAT inside interfaces)
  const [availableInsideInterfaces, setAvailableInsideInterfaces] = useState<InsideInterfaceOption[]>([]);
  const [interfaceMode, setInterfaceMode] = useState<'all' | 'specific'>('all');
  const [selectedInsideInterfaces, setSelectedInsideInterfaces] = useState<string[]>([]);
  const [loadingInsideInterfaces, setLoadingInsideInterfaces] = useState(false);

  // Map API fields to form fields
  const config = subscription?.configuration || {};
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    logicalInterface: '',
    sourceNetwork: '',
    translatedAddress: '',
    protocol: 'Any',
  });

  // Reset form when subscription changes or modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: subscription?.subscription_name || subscription?.name || '',
        description: subscription?.description || '',
        logicalInterface: subscription?.logical_port_id || config?.out_interface_id || lockedInterfaceId || '',
        sourceNetwork: config?.source_network || config?.in_ip || '',
        translatedAddress: config?.translated_address || config?.out_ip || '',
        protocol: config?.protocol || 'Any',
      });
      // Reset inside interface selection
      setInterfaceMode(config?.interface_mode || 'all');
      setSelectedInsideInterfaces(config?.inside_interfaces || []);
    }
  }, [isOpen, subscription, lockedInterfaceId]);

  // Fetch logical interfaces when modal opens
  useEffect(() => {
    const fetchInterfaces = async () => {
      if (!isOpen || !edgeId) return;

      try {
        setLoading(true);
        const response = await edgeService.listLogicalInterfaces(edgeId);
        // Handle nested response structure: response.data.items or response.items or response
        const interfaces = response?.data?.items || response?.items || response || [];
        setLogicalInterfaces(interfaces);
      } catch (err: any) {
        console.error('Failed to fetch logical interfaces:', err);
        setLogicalInterfaces([]);
      } finally {
        setLoading(false);
      }
    };

    fetchInterfaces();
  }, [isOpen, edgeId]);

  // Fetch available inside interfaces (physical + WireGuard) for NAT inside selection
  useEffect(() => {
    const fetchInsideInterfaces = async () => {
      if (!isOpen || !edgeId) return;

      try {
        setLoadingInsideInterfaces(true);
        // Fetch physical interfaces
        const physicalResponse = await edgeService.getPhysicalInterfaces(edgeId);
        const physicalInterfaces = (physicalResponse.items || [])
          .filter((iface: any) => iface.configuredState === true)
          .map((iface: any) => ({
            name: iface.name,
            type: 'physical' as const,
            enabled: iface.operationalState === 'Up' || iface.configuredState,
          }));

        // Add WireGuard interfaces (wg0 for IoT, wg1 for Apps)
        const wgInterfaces: InsideInterfaceOption[] = [
          { name: 'wg0', type: 'wireguard', enabled: true },
          { name: 'wg1', type: 'wireguard', enabled: true },
        ];

        setAvailableInsideInterfaces([...physicalInterfaces, ...wgInterfaces]);
      } catch (error) {
        console.error('Failed to fetch inside interfaces:', error);
        // Fallback to default interfaces
        setAvailableInsideInterfaces([
          { name: 'G0', type: 'physical', enabled: true },
          { name: 'G1', type: 'physical', enabled: true },
          { name: 'G2', type: 'physical', enabled: true },
          { name: 'wg0', type: 'wireguard', enabled: true },
          { name: 'wg1', type: 'wireguard', enabled: true },
        ]);
      } finally {
        setLoadingInsideInterfaces(false);
      }
    };

    fetchInsideInterfaces();
  }, [isOpen, edgeId]);

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Handle interface mode change (all vs specific)
  const handleInterfaceModeChange = (mode: 'all' | 'specific') => {
    setInterfaceMode(mode);
    if (mode === 'all') {
      // Clear specific selections when switching to "all"
      setSelectedInsideInterfaces([]);
    }
  };

  // Handle individual inside interface toggle
  const handleInsideInterfaceToggle = (interfaceName: string, checked: boolean) => {
    if (checked) {
      setSelectedInsideInterfaces([...selectedInsideInterfaces, interfaceName]);
    } else {
      setSelectedInsideInterfaces(selectedInsideInterfaces.filter(i => i !== interfaceName));
    }
  };

  // Filter for WAN interfaces only (interfaces with role 'WAN' or port_type 'WAN')
  const wanInterfaces = logicalInterfaces.filter(
    (iface) =>
      iface.role?.toUpperCase() === 'WAN' ||
      iface.port_type?.toUpperCase() === 'WAN' ||
      iface.name?.toLowerCase().includes('wan')
  );

  // Validation. In draft mode the parent WAN doesn't exist server-side yet, so
  // the interface-selection requirement is satisfied by the draft itself.
  const hasValidInsideInterfaces = interfaceMode === 'all' || selectedInsideInterfaces.length > 0;
  const isValid = formData.name.trim() && (isDraft || formData.logicalInterface) && formData.sourceNetwork.trim() && hasValidInsideInterfaces;
  const hasWanInterfaces = isDraft || wanInterfaces.length > 0;

  const handleSubmit = () => {
    if (!isValid || !hasWanInterfaces) return;

    // Find the selected interface to get its name. In draft mode the interface
    // isn't in the server list yet — use the draft's name instead.
    const selectedInterface = logicalInterfaces.find(i => i.id === formData.logicalInterface);
    const interfaceName = isDraft ? draftInterface!.name : (selectedInterface?.name || '');

    // Map form data to API format
    const apiData = {
      type: 'source_nat',
      subscription_type: 'source_nat',
      subscription_name: formData.name,
      description: formData.description,
      logical_port_id: isDraft ? '' : formData.logicalInterface,
      configuration: {
        type: 'outside', // Mark as outside interface for SNAT
        snat_type: 'outside',
        source_network: formData.sourceNetwork,
        in_ip: formData.sourceNetwork, // Legacy field
        translated_address: formData.translatedAddress || '', // Empty = use interface IP
        out_ip: formData.translatedAddress || '',
        outbound_interface: interfaceName,
        out_interface_id: isDraft ? '' : formData.logicalInterface,
        out_interface_name: interfaceName,
        protocol: formData.protocol,
        // Inside interface configuration
        interface_mode: interfaceMode,
        inside_interfaces: interfaceMode === 'specific' ? selectedInsideInterfaces : [],
      },
    };

    onSave(apiData);
    onClose();
  };

  const footer = (
    <div className="flex items-center justify-end space-x-3">
      <button onClick={onClose} className="btn-secondary">
        Cancel
      </button>
      <button
        onClick={handleSubmit}
        className="btn-primary inline-flex items-center space-x-2"
        disabled={!isValid || !hasWanInterfaces || loading}
      >
        <Save className="w-4 h-4" />
        <span>{mode === 'add' ? 'Create Source NAT' : 'Save Changes'}</span>
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'add' ? 'Add Source NAT Rule' : `Edit Source NAT: ${subscription?.subscription_name || subscription?.name || ''}`}
      size="lg"
      footer={footer}
    >
      <div className="space-y-6">
        {/* Warning if no WAN interfaces */}
        {!loading && !hasWanInterfaces && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start space-x-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-900">No WAN Interfaces Available</p>
              <p className="text-sm text-amber-700 mt-1">
                Source NAT requires a WAN interface. Please configure a logical interface with WAN role first.
              </p>
            </div>
          </div>
        )}

        {/* Info Box */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start space-x-3">
          <Globe className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-green-900">Source NAT (Outbound NAT)</p>
            <p className="text-sm text-green-700 mt-1">
              Translate internal IP addresses to the WAN interface IP when accessing the internet.
            </p>
          </div>
        </div>

        {/* Form Fields */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="e.g., Corporate Outbound NAT"
              className="input-base w-full"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="e.g., NAT for corporate network"
              className="input-base w-full"
            />
          </div>

          {!lockedInterfaceId && !isDraft && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                WAN Interface <span className="text-red-500">*</span>
              </label>
              {loading ? (
                <div className="input-base w-full bg-gray-50 text-gray-500">Loading interfaces...</div>
              ) : (
                <select
                  value={formData.logicalInterface}
                  onChange={(e) => handleChange('logicalInterface', e.target.value)}
                  className="input-base w-full"
                  required
                  disabled={!hasWanInterfaces}
                >
                  <option value="">Select WAN interface</option>
                  {wanInterfaces.map((iface) => (
                    <option key={iface.id} value={iface.id}>
                      {iface.name} {iface.ipv4 && iface.ipv4 !== '-' ? `(${iface.ipv4})` : ''} {iface.physicalInterface ? `[${iface.physicalInterface}]` : ''} {iface.description ? `- ${iface.description}` : ''}
                    </option>
                  ))}
                </select>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Traffic from the source network will be NATed through this interface
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Source Network <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.sourceNetwork}
              onChange={(e) => handleChange('sourceNetwork', e.target.value)}
              placeholder="e.g., 192.168.1.0/24"
              className="input-base w-full"
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              Internal network to NAT (CIDR notation). Use 0.0.0.0/0 for all traffic.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Translated Address</label>
            <input
              type="text"
              value={formData.translatedAddress}
              onChange={(e) => handleChange('translatedAddress', e.target.value)}
              placeholder="Leave blank to use interface IP (recommended)"
              className="input-base w-full"
            />
            <p className="mt-1 text-xs text-gray-500">
              Public IP to translate to. Leave blank to use the WAN interface's IP address (masquerading).
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Protocol</label>
            <select
              value={formData.protocol}
              onChange={(e) => handleChange('protocol', e.target.value)}
              className="input-base w-full"
            >
              <option value="Any">Any</option>
              <option value="TCP">TCP</option>
              <option value="UDP">UDP</option>
              <option value="ICMP">ICMP</option>
            </select>
          </div>

          {/* Inside Interfaces Section */}
          <div className="border-t pt-4 mt-4">
            <div className="flex items-center space-x-2 mb-3">
              <Network className="w-5 h-5 text-gray-400" />
              <label className="block text-sm font-medium text-gray-700">
                NAT Inside Interfaces
              </label>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Select which interfaces should have their traffic NATed through the WAN interface.
            </p>

            {/* Interface Mode Selection */}
            <div className="space-y-3">
              <label className="flex items-center space-x-3">
                <input
                  type="radio"
                  name="interfaceMode"
                  value="all"
                  checked={interfaceMode === 'all'}
                  onChange={() => handleInterfaceModeChange('all')}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <span className="text-sm text-gray-700">
                  <span className="font-medium">All LAN Interfaces</span>
                  <span className="text-gray-500 ml-1">(Automatically includes all LAN role interfaces + WireGuard)</span>
                </span>
              </label>

              <label className="flex items-center space-x-3">
                <input
                  type="radio"
                  name="interfaceMode"
                  value="specific"
                  checked={interfaceMode === 'specific'}
                  onChange={() => handleInterfaceModeChange('specific')}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <span className="text-sm text-gray-700">
                  <span className="font-medium">Specific Interfaces</span>
                  <span className="text-gray-500 ml-1">(Choose exactly which interfaces to NAT)</span>
                </span>
              </label>
            </div>

            {/* Specific Interface Selection (shown when mode is 'specific') */}
            {interfaceMode === 'specific' && (
              <div className="mt-4 ml-7 p-4 bg-gray-50 rounded-lg border border-gray-200">
                {loadingInsideInterfaces ? (
                  <div className="text-sm text-gray-500">Loading interfaces...</div>
                ) : (
                  <div className="space-y-3">
                    {/* Individual interface checkboxes */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {availableInsideInterfaces.map((iface) => (
                        <label key={iface.name} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={selectedInsideInterfaces.includes(iface.name)}
                            onChange={(e) => handleInsideInterfaceToggle(iface.name, e.target.checked)}
                            disabled={!iface.enabled}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                          />
                          <span className={`text-sm ${!iface.enabled ? 'text-gray-400' : 'text-gray-700'}`}>
                            {iface.name}
                            <span className="text-xs text-gray-400 ml-1">
                              ({iface.type === 'wireguard' ? 'WG' : 'Phys'})
                            </span>
                          </span>
                        </label>
                      ))}
                    </div>

                    {availableInsideInterfaces.length === 0 && (
                      <p className="text-sm text-gray-500">No interfaces available</p>
                    )}

                    {selectedInsideInterfaces.length === 0 && (
                      <p className="text-sm text-amber-600 mt-2">
                        Please select at least one inside interface
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default SourceNATModal;
